import type { Feed, FeedEntry, UIComponent } from '~/services/layout';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getLimit, isEmptyArray } from '~/services/layout/layout';
import logger from '~/services/logger';
import { requestJsonData } from '~/utils/request-json-data';
import { v4 as uuidv4 } from 'uuid';
import { useSearchParams } from '@remix-run/react';
import {
  selectEntryParentBehavior,
  selectEntryParentPreferenceEditorOptions,
  getSelectedCell,
  getUnselectedCell,
  executeCellActions,
} from './helpers';
import type { Action } from '../actions/types';

const PREFERENCE_EDITOR_ROLE: string = 'preference_editor';
const PREFERENCE_EDITOR_TIMEOUT: number = 12_000;
const channelName: string = PREFERENCE_EDITOR_ROLE + uuidv4();

type PreferenceEditorOptions = {
  isInitialLoad: boolean;
  feedEntryExtensionsTag: string;
  role: string | undefined;
  byClick: boolean;
};

export function usePreferenceEditor(
  feedEntry: FeedEntry,
  uiComponent: UIComponent
): {
  isPreferenceEditor: boolean;
  isBehaviorMultiActionFeedEntry: boolean;
  isPreferenceEditorCellAndUnselected: boolean;
  isPreferenceEditorCellAndSelected: boolean;
  preferenceEditorActionFunction: Function;
} {
  const role: string | undefined = feedEntry?._role;
  const {
    selectMode: behaviorSelectMode,
    currentSelection: behaviorCurrentSelection,
  } = selectEntryParentBehavior(feedEntry);

  const isPreferenceEditor: boolean = role === PREFERENCE_EDITOR_ROLE;

  const isBehaviorMultiActionFeedEntry: boolean =
    behaviorSelectMode !== undefined && !isPreferenceEditor;

  const feedEntryExtensionsTag: string | undefined =
    feedEntry?.extensions?.tag || feedEntry?.id;

  const preference_editor_options =
    selectEntryParentPreferenceEditorOptions(feedEntry);

  const initialValue = preference_editor_options?.initial_value;

  const isInitialValueSelected: boolean =
    typeof initialValue === 'string' &&
    typeof feedEntryExtensionsTag === 'string' &&
    initialValue.toLowerCase() === feedEntryExtensionsTag.toLowerCase();

  const [selected, setSelected] = useState<string>(initialValue);

  // Only create BroadcastChannel on the client (not during SSR)
  // This prevents MessagePort leaks on the server
  // Use useMemo to ensure it's only created once per component instance
  const selectedChannel = useMemo(() => {
    return typeof window !== 'undefined'
      ? new BroadcastChannel(channelName)
      : null;
  }, []);

  const isLoadedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isPreferenceEditor || isLoadedRef.current || !selectedChannel) return;

    isLoadedRef.current = true;

    const onMessage = (event: MessageEvent) => {
      setSelected(event.data?.feedEntryExtensionsTag);
    };

    if (isInitialValueSelected) {
      selectedChannel.postMessage({
        isInitialLoad: !!initialValue,
        feedEntryExtensionsTag: initialValue,
        role,
        byClick: false,
      });
    }

    selectedChannel.addEventListener('message', onMessage);
    return () => {
      if (selectedChannel) {
        selectedChannel.removeEventListener('message', onMessage);
        try {
          selectedChannel.close();
        } catch {}
      }
    };
  }, []);

  const preferenceEditorActionFunction: Function = async () => {
    if (isBehaviorMultiActionFeedEntry) {
      if (
        feedEntry?.extensions?.tap_actions &&
        Array.isArray(feedEntry.extensions.tap_actions?.actions)
      ) {
        executeCellActions(
          feedEntry.extensions.tap_actions as { actions?: Action[] },
          {
            feedEntry,
            uiComponent,
          }
        );
      }
      return;
    }

    if (!isPreferenceEditor || !feedEntryExtensionsTag || !selectedChannel)
      return;

    if (feedEntryExtensionsTag === selected) return;

    setSelected(feedEntryExtensionsTag);

    selectedChannel.postMessage({
      isInitialLoad: false,
      feedEntryExtensionsTag: feedEntryExtensionsTag,
      role,
      byClick: true,
    });
  };

  const cellArgs = {
    isPreferenceEditor,
    isBehaviorMultiActionFeedEntry,
    behaviorCurrentSelection,
    selected,
    feedEntryExtensionsTag,
  };

  return {
    isPreferenceEditor,
    isBehaviorMultiActionFeedEntry,
    isPreferenceEditorCellAndSelected: getSelectedCell(cellArgs),
    isPreferenceEditorCellAndUnselected: getUnselectedCell(cellArgs),
    preferenceEditorActionFunction,
  };
}

export function usePreferenceEditorPayload({
  feed,
  uiComponent,
  setFeedEntries,
}: {
  feed: Feed | undefined;
  uiComponent: UIComponent;
  setFeedEntries: Function;
}): void {
  // Only create BroadcastChannel on the client (not during SSR)
  // This prevents MessagePort leaks on the server
  // Use useMemo to ensure it's only created once per component instance
  const payloadChannel = useMemo(() => {
    return typeof window !== 'undefined'
      ? new BroadcastChannel(channelName)
      : null;
  }, []);

  // Storing entries in a ref for accessing from the timeout
  const serverLoadedFeedEntryRef = useRef<FeedEntry[]>(
    feed?.entry?.slice(0, getLimit(uiComponent?.rules?.item_limit)) || []
  );

  // Instantly track feed changes to update the ref value
  serverLoadedFeedEntryRef.current =
    feed?.entry?.slice(0, getLimit(uiComponent?.rules?.item_limit)) || [];

  const role = feed?.extensions?.role;
  const isPreferenceEditor: boolean = role === PREFERENCE_EDITOR_ROLE;

  // Access current URL and check for screen-feed query param
  const [searchParams] = useSearchParams();
  const screenFeed = searchParams.get('screen-feed');

  const isPreferenceEditorByObserveStorageEndpointTag: boolean =
    getIsPreferenceEditorByObserveStorageEndpointTag(uiComponent);

  useEffect(() => {
    if (
      isPreferenceEditor ||
      !isPreferenceEditorByObserveStorageEndpointTag ||
      !payloadChannel
    )
      return;

    let isServerLoadedFeedEntry: boolean = true;

    const timeoutId = setTimeout(() => {
      if (!isServerLoadedFeedEntry) return;
      setFeedEntries(serverLoadedFeedEntryRef.current);
    });

    const onMessage = async (event: MessageEvent) => {
      try {
        isServerLoadedFeedEntry = false;

        const eventData: PreferenceEditorOptions = event?.data;

        if (!eventData) throw new Error(`No event data found`);

        const role = eventData?.role;

        if (role !== PREFERENCE_EDITOR_ROLE)
          throw new Error(`Role is not preference_editor`);

        const isInitialLoad: boolean = eventData?.isInitialLoad;

        if (!isInitialLoad && !eventData?.byClick) return;

        const feedPath: string | undefined = feed?._feedPath;

        const feedEntryExtensionsTag: string | undefined =
          eventData?.feedEntryExtensionsTag;

        if (!feedEntryExtensionsTag)
          throw new Error(`No feed entry extensions tag found`);

        const sessionStorageKey: string = `${feedPath}-${feedEntryExtensionsTag}`;

        const preferenceEditorJsonData: Feed | undefined =
          await requestJsonData({
            url: '/api/preference-editor',
            method: 'POST',
            body: JSON.stringify({
              feedUrl: feedPath,
              feedEntryExtensionsTag,
              role,
              screenFeed,
            }),
            timeout: PREFERENCE_EDITOR_TIMEOUT,
            sessionStorageKey,
          });

        const entry: FeedEntry[] | undefined = preferenceEditorJsonData?.entry;

        if (isEmptyArray(entry))
          throw new Error(`No entry found in preference editor response`);

        setFeedEntries(() => {
          const itemLimit: number | undefined = uiComponent?.rules?.item_limit;
          if (!itemLimit) return entry;
          return entry!.slice(0, getLimit(itemLimit));
        });
      } catch (error: any) {
        isServerLoadedFeedEntry = true;
        logger.info(`Error in preference editor payload: ${error.message}`);
      }
    };

    payloadChannel.addEventListener('message', onMessage);
    return () => {
      clearTimeout(timeoutId);
      if (payloadChannel) {
        payloadChannel.removeEventListener('message', onMessage);
        try {
          payloadChannel.close();
        } catch {}
      }
    };
  }, []);
}

export function getIsPreferenceEditorByObserveStorageEndpointTag(
  uiComponent: UIComponent
): boolean {
  const tags = uiComponent?._tags;
  if (isEmptyArray(tags)) return false;
  if (tags?.includes('observe_storage')) return true;
  return false;
}

export function getPreferenceEditorComponentVisibility({
  feed,
  entries,
}: {
  feed: Feed | undefined;
  entries: FeedEntry[];
}): boolean {
  if (feed?.type?.value === 'offers-groups' && isEmptyArray(entries))
    return true;

  return false;
}
